<template>
    <Layout>
        <div class="col-12">
            <Breadcrumb :items="items" />
            <div class="row mt-3 mb-3">
                <div class="col-sm-8">
                    <b-row>
                        <!-- Período -->
                        <b-col md="5">
                            <b-form-group
                                :label="i18nPeriod"
                                class="required label-pdv"
                                label-for="filter-date"
                            >
                                <date-range-picker
                                    :value.sync="filters.date"
                                    :max-date="new Date()"
                                    :disabled="loading"
                                    :disable-clean-button="true"
                                />
                            </b-form-group>
                        </b-col>
                        <!-- Unidade de Negócios -->
                        <b-col md="5">
                            <b-form-group
                                :label="i18nBusinessUnit"
                                class="label-pdv"
                                label-for="filter-business-unit"
                            >
                                <multiSelectWithService
                                    :id="'filter-business-unit'"
                                    ref="businessUnitMultiselect"
                                    v-model="filters.business_units"
                                    :service="'business-units'"
                                    :searchable="true"
                                    :multiple="true"
                                    :parameters="filterParameters"
                                    :disabled="loading"
                                />
                            </b-form-group>
                        </b-col>
                    </b-row>
                </div>

                <!-- Botões de Filtro -->
                <div class="col-sm-4 align-self-center">
                    <div class="d-flex justify-content-end">
                        <more-filters-label-button class="col-sm-6" :disabled="loading" @onClick="showFilterModal" />
                        <filter-button class="col-sm-6 col-md-6" :disabled="loading" @onClick="filter" />
                    </div>
                </div>
            </div>

            <!-- Total em Números-->
            <work-schedule-statement-resume ref="workScheduleStatementResume" />

            <!-- PDVs Percentagem -->
            <work-schedule-statement-percentage ref="workScheduleStatementPercentage" />

            <!-- Gráficos -->
            <b-row>
                <b-col md="6">
                    <!-- Visitas Contratadas vs Realizadas -->
                    <work-schedule-statement-type-chart ref="workScheduleStatementTypeChart" />
                </b-col>
                <b-col md="6">
                    <!-- Desempenho (%) -->
                    <work-schedule-statement-chain-chart ref="workScheduleStatementChainChart" />
                </b-col>
            </b-row>

            <!-- Tabela -->
            <PageHeader :title="i18nDetailedMonitoring" class="table-header-title" :show-slot="true" >
                <export-button
                    v-if="$can('CREATE', 'EXPORTJOB')"
                    :type="activeListTab === 0 ? 'work_schedule_statement_pdv' : 'work_schedule_statement_profile'"
                    :create-parameters="createExportAdditionalParameters"
                    :has-vuetable-data="hasDataLoaded"
                />
            </PageHeader>
            <div class="card card-pdv">
                <div class="card-body">
                    <b-tabs
                        id="work-schedule-statement-tabs"
                        v-model="activeListTab"
                        class="tabs-pdv"
                    >
                        <b-tab :title="i18nPdvTab">
                            <api-mode-vuetable
                                ref="vuetablePdv"
                                :fetch-data="fetch"
                                :additional-parameters="additionalParametersPdv"
                                :fields="fieldsPdv"
                                :sort-order="sortOrder"
                                defer-load
                            >
                                <template slot="done_percentage" slot-scope="props">
                                    <progress-bar-table :value.sync="props.rowData.done_percentage" />
                                </template>
                                <template slot="done_percentage_with_justifications" slot-scope="props">
                                    <progress-bar-table :value.sync="props.rowData.done_percentage_with_justifications" />
                                </template>
                                <template slot="visits_frequency" slot-scope="props">
                                    {{ props.rowData.visits_frequency_label !== null && props.rowData.visits_frequency_label !== '' ? props.rowData.visits_frequency_label : props.rowData.visits_frequency }}
                                </template>
                                <template slot="last_visit" slot-scope="props">
                                    {{ formatDateShort(props.rowData.last_visit) || '-' }}
                                </template>
                                <template slot="days_without_visits" slot-scope="props">
                                    {{ props.rowData.days_without_visits !== null ? props.rowData.days_without_visits : '-' }}
                                </template>
                            </api-mode-vuetable>
                        </b-tab>
                        <b-tab :title="i18nProfileTab">
                            <api-mode-vuetable
                                ref="vuetableProfile"
                                :fetch-data="fetch"
                                :additional-parameters="additionalParametersProfile"
                                :fields="fieldsProfile"
                                :sort-order="sortOrder"
                                defer-load
                            >
                                <template slot="done_percentage" slot-scope="props">
                                    <progress-bar-table :value.sync="props.rowData.done_percentage" />
                                </template>
                            </api-mode-vuetable>
                        </b-tab>
                    </b-tabs>
                </div>
            </div>
        </div>
        <work-schedule-statement-filter-modal
            ref="workScheduleStatementFilterModal"
            :handle-submit="filter"
        />
    </Layout>
</template>

<script>
    import Breadcrumb from '@/src/components/Breadcrumb.vue'
import DateRangePicker from '@/src/components/DateRangePicker'
import ExportButton from '@/src/components/ExportButton.vue'
import FilterButton from '@/src/components/FilterButton'
import MoreFiltersLabelButton from '@/src/components/MoreFiltersLabelButton'
import PageHeader from '@/src/components/PageHeader.vue'
import workScheduleStatementChainChart from '@/src/router/views/dashboards/workScheduleStatement/workScheduleStatementChainChart.vue'
import workScheduleStatementFilterModal from '@/src/router/views/dashboards/workScheduleStatement/workScheduleStatementFilterModal.vue'
import workScheduleStatementPercentage from '@/src/router/views/dashboards/workScheduleStatement/workScheduleStatementPercentage.vue'
import workScheduleStatementResume from '@/src/router/views/dashboards/workScheduleStatement/workScheduleStatementResume.vue'
import workScheduleStatementTypeChart from '@/src/router/views/dashboards/workScheduleStatement/workScheduleStatementTypeChart.vue'
import WorkScheduleStatementService from '@/src/services/dashboards/widgets/WorkScheduleStatementService'
import ApiModeVuetable from '@components/ApiModeVuetable'
import ProgressBarTable from '@components/ProgressBarTable'
import Layout from '@layouts/main'
import multiSelectWithService from '@src/components/multiSelectWithService.vue'
import filterVuetable from '@src/mixins/filterVuetable'
import vuetableFormatter from '@src/mixins/vuetableFormatters'
import moment from 'moment'

    const i18nKey = 'DASHBOARDS'
    const i18nCommon = 'COMMON'
    const i18nRelations = 'RELATIONS'

    export default {
        components: {
            Layout,
            ApiModeVuetable,
            ProgressBarTable,
            Breadcrumb,
            PageHeader,
            DateRangePicker,
            FilterButton,
            MoreFiltersLabelButton,
            multiSelectWithService,
            workScheduleStatementFilterModal,
            workScheduleStatementResume,
            workScheduleStatementPercentage,
            workScheduleStatementTypeChart,
            workScheduleStatementChainChart,
            ExportButton,
        },
        mixins: [
            filterVuetable,
            vuetableFormatter,
        ],
        data() {
            return {
                i18nKey,
                i18nCommon,
                loading: false,
                additionalParameters: {},
                perPage: 10,
                sortOrder: [
                    {
                        field: 'profile',
                        direction: 'desc',
                    },
                ],
                fieldsPdv: [
                    {
                        name: 'pointOfSale',
                        title: this.getI18n(i18nRelations, 'TYPES.pointOfSale'),
                        sortField: 'pointOfSale',
                    },
                    {
                        name: 'visits_booked',
                        title: this.getI18n(i18nKey, 'TITLES.visits_booked'),
                        sortField: 'visits_booked',
                    },
                    {
                        name: 'visits_carried_out',
                        title: this.getI18n(i18nKey, 'TITLES.visits_carried_out'),
                        sortField: 'visits_carried_out',
                    },
                    {
                        name: 'visits_missing',
                        title: this.getI18n(i18nKey, 'TITLES.visits_missing'),
                        sortField: 'visits_missing',
                    },
                    {
                        name: 'visits_justified',
                        title: this.getI18n(i18nKey, 'TITLES.visits_justified'),
                        sortField: 'visits_justified',
                    },
                    {
                        name: 'visits_frequency',
                        title: this.getI18n(i18nKey, 'TITLES.visits_frequency'),
                        sortField: 'visits_frequency',
                    },
                    {
                        name: 'last_visit',
                        title: this.getI18n(i18nCommon, 'last_visit'),
                        sortField: 'last_visit',
                    },
                    {
                        name: 'days_without_visits',
                        title: this.getI18n(i18nCommon, 'days_without_visits'),
                        sortField: 'days_without_visits',
                    },
                    {
                        name: 'profile',
                        title: this.getI18n(i18nCommon, 'profile'),
                        sortField: 'profile',
                    },
                    {
                        name: 'done_percentage',
                        title: this.getI18n(i18nKey, 'TITLES.done_percentage'),
                        sortField: 'done_percentage',
                    },
                ],
                fieldsProfile: [
                    {
                        name: 'profile',
                        title: this.getI18n(i18nCommon, 'profile'),
                        sortField: 'profile',
                    },
                    {
                        name: 'pos_active',
                        title: this.getI18n(i18nKey, 'TITLES.pos_active'),
                        sortField: 'pos_active',
                    },
                    {
                        name: 'pos_routed',
                        title: this.getI18n(i18nKey, 'TITLES.pos_routed'),
                        sortField: 'pos_routed',
                    },
                    {
                        name: 'visits_booked',
                        title: this.getI18n(i18nKey, 'TITLES.visits_booked'),
                        sortField: 'visits_booked',
                    },
                    {
                        name: 'visits_carried_out',
                        title: this.getI18n(i18nKey, 'TITLES.visits_carried_out'),
                        sortField: 'visits_carried_out',
                    },
                    {
                        name: 'visits_missing',
                        title: this.getI18n(i18nKey, 'TITLES.visits_missing'),
                        sortField: 'visits_missing',
                    },
                    {
                        name: 'visits_justified',
                        title: this.getI18n(i18nKey, 'TITLES.visits_justified'),
                        sortField: 'visits_justified',
                    },
                    {
                        name: 'done_percentage',
                        title: this.getI18n(i18nKey, 'TITLES.done_percentage'),
                        sortField: 'done_percentage',
                    },
                ],
                filterParameters: {
                    per_page: 1000,
                    page: 1,
                    orderBy: 'name',
                    sortedBy: 'asc',
                },
                filters: {
                    date: {
                        start: moment().startOf('day').add(-30, 'days').toDate(),
                        end: moment().startOf('day').toDate()
                    },
                    business_units: [],
                    regionals: [],
                    channels: [],
                    chains: [],
                    flags: [],
                },
                activeListTab: 0,
                listFilterEpoch: 0,
                pdvListLoadedEpoch: -1,
                profileListLoadedEpoch: -1,
            }
        },
        computed: {
            items() {
                return [
                    {
                        text: 'Home',
                        href: '/',
                    },
                    {
                        text: this.getI18nModified({
                            prefix: i18nKey,
                            suffix: 'TITLES.dashboards',
                            modifier: 2,
                        }),
                        active: true,
                    },
                    {
                        text: this.i18nWorkScheduleStatement,
                        active: true,
                    },
                ]
            },
            i18nWorkScheduleStatement() {
                return this.getI18n(i18nKey, 'TITLES.work_schedule_statement')
            },
            i18nPeriod() {
                return this.getI18n(i18nCommon, 'period')
            },
            i18nBusinessUnit() {
                return this.getI18n(i18nCommon, 'business_unit')
            },
            i18nDetailedMonitoring() {
                return this.getI18n(i18nKey, 'TITLES.detailed_monitoring')
            },
            i18nPdvTab() {
                return this.getI18n('POS', 'TITLES.pos_alias')
            },
            i18nProfileTab() {
                return this.getI18n(i18nCommon, 'profile')
            },
            additionalParametersPdv() {
                return { ...this.additionalParameters, group_by: 'pdv' }
            },
            additionalParametersProfile() {
                return { ...this.additionalParameters, group_by: 'profile' }
            },
        },
        watch: {
            activeListTab(newIdx) {
                this.$nextTick(() => this.refreshListTabIfStale(newIdx))
            },
        },
        created() {
            this.additionalParameters = this.createAdditionalParameters()
        },
        mounted() {
            this.filter()
        },
        methods: {
            hasDataLoaded() {
                if (this.activeListTab === 0) {
                    return this.$refs.vuetablePdv?.$refs?.vuetable?.tableData?.length > 0
                } else {
                    return this.$refs.vuetableProfile?.$refs?.vuetable?.tableData?.length > 0
                }
            },
            createExportAdditionalParameters() {
                return this.activeListTab === 0 ? this.additionalParametersPdv : this.additionalParametersProfile;
            },
            createAdditionalParameters() {
                const period = this.formatFilterDate(this.filters.date)
                return this.formatParameters({
                    business_unit_id: this.filters.business_units.map((bu) => bu.id),
                    regional_id: this.filters.regionals.map((regional) => regional.id),
                    channel_id: this.filters.channels.map((channel) => channel.id),
                    chain_id: this.filters.chains.map((chain) => chain.id),
                    flag_id: this.filters.flags.map((flag) => flag.id),
                    start: period.from,
                    end: period.to,
                })
            },
            filter(appliedFilters) {
                if (appliedFilters) {
                    this.filters = appliedFilters
                }
                this.additionalParameters = this.createAdditionalParameters()
                this.listFilterEpoch += 1
                this.refresh()
            },
            showFilterModal() {
                this.$nextTick(() => this.$refs.workScheduleStatementFilterModal.showModal(this.filters))
            },
            fetch(url, params) {
                return WorkScheduleStatementService.fetchVuetable('list', params)
            },
            refresh() {
                this.loading = true

                this.$refs.workScheduleStatementResume.refresh(this.additionalParameters)
                this.$refs.workScheduleStatementPercentage.refresh(this.additionalParameters)
                this.$refs.workScheduleStatementTypeChart.refresh(this.additionalParameters)
                this.$refs.workScheduleStatementChainChart.refresh(this.additionalParameters)
                this.refreshStaleListForActiveTabOnly()

                this.loading = false
            },
            refreshStaleListForActiveTabOnly() {
                this.$nextTick(() => {
                    this.refreshListTabIfStale(this.activeListTab)
                })
            },
            refreshListTabIfStale(tabIndex) {
                if (tabIndex === 0 && this.pdvListLoadedEpoch < this.listFilterEpoch && this.$refs.vuetablePdv) {
                    this.$refs.vuetablePdv.refresh()
                    this.pdvListLoadedEpoch = this.listFilterEpoch
                }
                if (tabIndex === 1 && this.profileListLoadedEpoch < this.listFilterEpoch && this.$refs.vuetableProfile) {
                    this.$refs.vuetableProfile.refresh()
                    this.profileListLoadedEpoch = this.listFilterEpoch
                }
            },
        },
    }
</script>

<style>
    .table-header-title h4 {
        font-size: 20px !important;
        font-weight: 600 !important;
        text-align: left !important;
    }
</style>
